import React from 'react'
import { cn } from '@/lib/utils'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helperText?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, leftIcon, rightIcon, className, id, required, 'aria-describedby': ariaDescribedByProp, ...props }, ref) => {
    const reactId = React.useId()
    const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : reactId)
    const errorId = error ? `${inputId}-error` : undefined
    const helperId = helperText && !error ? `${inputId}-helper` : undefined
    const describedBy = [ariaDescribedByProp, errorId, helperId].filter(Boolean).join(' ') || undefined

    return (
      <div className="w-full space-y-1.5">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-ink-300">
            {label}
            {required && (
              <span aria-hidden="true" className="ml-1 text-white/[0.62]">*</span>
            )}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            required={required}
            aria-required={required || undefined}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            className={cn(
              'input-field',
              leftIcon && 'pl-9',
              rightIcon && 'pr-9',
              error && 'border-blush/70 focus:border-blush focus:ring-blush/50',
              className
            )}
            {...props}
          />
          {rightIcon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400">
              {rightIcon}
            </div>
          )}
        </div>
        {error && (
          <p id={errorId} role="alert" aria-live="polite" className="text-sm text-blush">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={helperId} className="text-sm text-white/[0.62]">
            {helperText}
          </p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  helperText?: string
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, helperText, className, id, required, 'aria-describedby': ariaDescribedByProp, ...props }, ref) => {
    const reactId = React.useId()
    const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : reactId)
    const errorId = error ? `${inputId}-error` : undefined
    const helperId = helperText && !error ? `${inputId}-helper` : undefined
    const describedBy = [ariaDescribedByProp, errorId, helperId].filter(Boolean).join(' ') || undefined

    return (
      <div className="w-full space-y-1.5">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-ink-300">
            {label}
            {required && (
              <span aria-hidden="true" className="ml-1 text-white/[0.62]">*</span>
            )}
          </label>
        )}
        <textarea
          ref={ref}
          id={inputId}
          required={required}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            'input-field resize-none min-h-[80px]',
            error && 'border-blush/70 focus:border-blush focus:ring-blush/50',
            className
          )}
          {...props}
        />
        {error && (
          <p id={errorId} role="alert" aria-live="polite" className="text-sm text-blush">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={helperId} className="text-sm text-white/[0.62]">
            {helperText}
          </p>
        )}
      </div>
    )
  }
)

Textarea.displayName = 'Textarea'
